import React, { useEffect, useState } from 'react';
import { Button, message, Space, Table, Tag, Tooltip, Image } from 'antd';
import { DeleteOutlined, RollbackOutlined, LinkOutlined, CopyOutlined } from '@ant-design/icons';
import type { ColumnsType, TableProps } from 'antd/es/table';
import { getId } from '../helpers/file';
import ErrorBoundary from '../components/ErrorBoundary';
import { getDate, getTime } from '../utils/dateUtils';
import { isImg } from '../helpers/img';
import { getPreview } from '../utils/fileIconMap';
import { copyToClipboard } from '../helpers/clipboard';
import { useTranslation } from '../hooks/useTranslation';
import './Trash.css';

interface TrashRecord {
  id: number;
  fileName: string;
  url: string;
  rawUrl?: string;
  contentType: string;
  size: string;
  time: string;
  dir?: string;
  storageType?: string;
  illicit?: number;
  illicitDetails?: string;
  deletedTime?: number; // 删除时间戳（从后端返回）
}

const Trash: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState<TrashRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [userInfo, setUserInfo] = useState<Record<string, any>>({});
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // 计算距离彻底删除的剩余天数
  const calculateRemainingDays = (deletedTime: number | undefined): number => {
    if (!deletedTime) return -1;
    
    const TRASH_EXPIRE_DAYS = 7;
    const currentTime = Date.now();
    const expireTime = deletedTime + (TRASH_EXPIRE_DAYS * 24 * 60 * 60 * 1000);
    const remainingMillis = expireTime - currentTime;
    const remainingDays = Math.ceil(remainingMillis / (24 * 60 * 60 * 1000));
    
    return Math.max(0, remainingDays);
  };

  // 检查用户是否登录（需要有真实的登录用户 email，而不仅仅是匿名 uid）
  const checkLogin = () => {
    if (!isLoggedIn) {
      message.error(t('trash.loginRequired'));
      return false;
    }
    return true;
  };

  // 加载回收站列表
  const loadTrashList = async (currentPage: number = page) => {
    if (!checkLogin()) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/trash/list?page=${currentPage}&pageSize=${pageSize}`);
      const result = await response.json();
      
      if (result.data) {
        setList(result.data);
        setTotal(result.total || 0);
      } else {
        message.error(t('trash.loadFailed', { message: result.message || t('error.unknownError') }));
        setList([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('加载回收站失败:', error);
      message.error(t('trash.loadFailedNetwork'));
      setList([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // 恢复记录
  const handleRestore = async (id: number) => {
    if (!checkLogin()) return;

    try {
      const response = await fetch(`/trash/restore?id=${id}`, {
        method: 'POST',
      });
      const result = await response.json();

      if (result.success) {
        message.success(t('trash.restoreSuccess'));
        loadTrashList(page);
      } else {
        message.error(t('trash.restoreFailed', { message: result.message || t('error.unknownError') }));
      }
    } catch (error) {
      console.error('恢复失败:', error);
      message.error(t('trash.restoreFailedNetwork'));
    }
  };

  // 彻底删除
  const handlePermanentlyDelete = async (id: number) => {
    if (!checkLogin()) return;

    const confirmed = window.confirm(t('trash.deleteConfirm'));
    if (!confirmed) return;

    try {
      const response = await fetch(`/trash/delete?id=${id}`, {
        method: 'DELETE',
      });
      const result = await response.json();

      if (result.success) {
        message.success(t('trash.deleteSuccess'));
        loadTrashList(page);
      } else {
        message.error(t('trash.deleteFailed', { message: result.message || t('error.unknownError') }));
      }
    } catch (error) {
      console.error('删除失败:', error);
      message.error(t('trash.deleteFailedNetwork'));
    }
  };

  // 批量恢复
  const handleBatchRestore = async (ids: number[]) => {
    if (!checkLogin() || ids.length === 0) return;

    const confirmed = window.confirm(t('trash.batchRestoreConfirm', { count: ids.length }));
    if (!confirmed) return;

    try {
      const results = await Promise.all(
        ids.map(id =>
          fetch(`/trash/restore?id=${id}`, { method: 'POST' }).then(res => res.json())
        )
      );

      const successCount = results.filter(r => r.success).length;
      if (successCount === ids.length) {
        message.success(t('trash.batchRestoreSuccess', { count: successCount }));
      } else {
        message.warning(t('trash.batchRestorePartial', { success: successCount, total: ids.length }));
      }
      loadTrashList(page);
    } catch (error) {
      console.error('批量恢复失败:', error);
      message.error(t('trash.batchRestoreFailed'));
    }
  };

  // 批量彻底删除
  const handleBatchPermanentlyDelete = async (ids: number[]) => {
    if (!checkLogin() || ids.length === 0) return;

    const confirmed = window.confirm(
      t('trash.batchDeleteConfirm', { count: ids.length })
    );
    if (!confirmed) return;

    try {
      const results = await Promise.all(
        ids.map(id =>
          fetch(`/trash/delete?id=${id}`, { method: 'DELETE' }).then(res => res.json())
        )
      );

      const successCount = results.filter(r => r.success).length;
      if (successCount === ids.length) {
        message.success(t('trash.batchDeleteSuccess', { count: successCount }));
      } else {
        message.warning(t('trash.batchDeletePartial', { success: successCount, total: ids.length }));
      }
      loadTrashList(page);
    } catch (error) {
      console.error('批量删除失败:', error);
      message.error(t('trash.batchDeleteFailed'));
    }
  };

  // 加载用户信息并检查登录状态
  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const response = await fetch('/login/info');
        const result = await response.json();
        if (result) {
          setUserInfo(result);
          // 检查是否为真正的登录用户（有 username/email）
          const hasEmail = result.username || result.email;
          setIsLoggedIn(!!hasEmail);
          
          if (!hasEmail) {
            message.warning(t('trash.loginRequiredWarning'));
          }
        } else {
          setIsLoggedIn(false);
        }
      } catch (error) {
        console.error('加载用户信息失败:', error);
        setIsLoggedIn(false);
      }
    };
    loadUserInfo();
  }, []);

  // 初始加载
  useEffect(() => {
    // 只有在登录状态确定后才加载数据
    if (isLoggedIn) {
      loadTrashList(1);
    } else {
      setLoading(false);
    }
  }, [isLoggedIn]);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  // 表格列定义
  const columns: ColumnsType<TrashRecord> = [
    {
      title: t('trash.tablePreview'),
      dataIndex: 'url',
      key: 'preview',
      width: 80,
      render: (url: string, record: TrashRecord) => {
        // 判断是否为图片类型
        const isImage = record.contentType?.startsWith('image/') || isImg(record.fileName);
        
        if (isImage) {
          return (
            <Image
              src={url}
              width={50}
              height={50}
              style={{ objectFit: 'cover', borderRadius: '4px' }}
              placeholder
              fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=="
            />
          );
        }
        
        // 其他文件类型显示对应的文件图标
        const fileIcon = getPreview(record.fileName || record.url);
        return (
          <img 
            src={fileIcon} 
            alt={record.fileName}
            style={{ width: '50px', height: '50px', objectFit: 'contain' }}
          />
        );
      },
    },
    {
      title: t('trash.tableFileName'),
      dataIndex: 'fileName',
      key: 'fileName',
      ellipsis: {
        showTitle: true,
      },
      render: (fileName: string) => (
        <Tooltip title={fileName}>
          <span>{fileName}</span>
        </Tooltip>
      ),
    },
    {
      title: t('trash.tableAccessLink'),
      dataIndex: 'url',
      key: 'url',
      width: 150,
      ellipsis: true,
      render: (url: string) => (
        <Space size="small">
          <Tooltip title={t('trash.openInNewTab')}>
            <Button
              type="link"
              size="small"
              icon={<LinkOutlined />}
              onClick={() => window.open(url, '_blank')}
            >
              {t('trash.access')}
            </Button>
          </Tooltip>
          <Tooltip title={t('trash.copyLink')}>
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyToClipboard(url, t('trash.urlCopied'))}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: t('trash.tableFileSize'),
      dataIndex: 'size',
      key: 'size',
      width: 100,
    },
    {
      title: t('trash.tableType'),
      dataIndex: 'contentType',
      key: 'contentType',
      width: 120,
      render: (contentType: string) => {
        const type = contentType?.split('/')[0] || 'file';
        const colorMap: Record<string, string> = {
          image: 'blue',
          video: 'purple',
          application: 'orange',
          text: 'green',
        };
        return <Tag color={colorMap[type] || 'default'}>{type}</Tag>;
      },
    },
    {
      title: t('trash.tableDeleteTime'),
      dataIndex: 'time',
      key: 'time',
      width: 180,
      render: (time: string) => {
        if (!time) return '-';
        try {
          return (
            <span>
              {getDate(time)} {getTime(time)}
            </span>
          );
        } catch (error) {
          console.error('时间格式化错误:', error, time);
          return <span>{time}</span>;
        }
      },
    },
    {
      title: t('trash.tableRemainingDays'),
      key: 'remainingDays',
      width: 120,
      sorter: (a, b) => calculateRemainingDays(a.deletedTime) - calculateRemainingDays(b.deletedTime),
      render: (_: any, record: TrashRecord) => {
        const days = calculateRemainingDays(record.deletedTime);
        
        if (days < 0) return '-';
        
        let color = 'green';
        if (days <= 1) {
          color = 'red';
        } else if (days <= 3) {
          color = 'orange';
        } else if (days <= 5) {
          color = 'gold';
        }
        
        return (
          <Tag color={color}>
            {days === 0 ? t('trash.willDeleteSoon') : t('trash.daysLater', { days })}
          </Tag>
        );
      },
    },
    {
      title: t('trash.tableOperation'),
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_: any, record: TrashRecord) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<RollbackOutlined />}
            onClick={() => handleRestore(record.id)}
          >
            {t('trash.restore')}
          </Button>
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handlePermanentlyDelete(record.id)}
          >
            {t('trash.permanentlyDelete')}
          </Button>
        </Space>
      ),
    },
  ];

  // 表格行选择配置
  const rowSelection: TableProps<TrashRecord>['rowSelection'] = {
    selectedRowKeys: selectedIds,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedIds(selectedRowKeys as number[]);
    },
  };

  return (
    <ErrorBoundary>
      <div className="trash-page">
        <div className="trash-header">
          <h2>{t('trash.title')}</h2>
          <div className="trash-actions">
            <Space>
              <Tooltip title={t('trash.autoCleanTooltip')}>
                <Tag color="orange" icon="⏰">
                  {t('trash.autoCleanWarning')}
                </Tag>
              </Tooltip>
              {selectedIds.length > 0 && (
                <>
                  <Button
                    type="primary"
                    icon={<RollbackOutlined />}
                    onClick={() => {
                      handleBatchRestore(selectedIds);
                      setSelectedIds([]);
                    }}
                  >
                    {t('trash.restoreSelected', { count: selectedIds.length })}
                  </Button>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      handleBatchPermanentlyDelete(selectedIds);
                      setSelectedIds([]);
                    }}
                  >
                    {t('trash.deleteSelected', { count: selectedIds.length })}
                  </Button>
                </>
              )}
            </Space>
          </div>
        </div>

        <div className="trash-table-container">
          <Table<TrashRecord>
            columns={columns}
            dataSource={list}
            rowKey="id"
            loading={loading}
            rowSelection={rowSelection}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => t('trash.totalRecordsTrash', { total }),
              onChange: (newPage, newPageSize) => {
                setPage(newPage);
                setPageSize(newPageSize || pageSize);
                loadTrashList(newPage);
              },
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            locale={{
              emptyText: loading 
                ? t('common.loading') 
                : isLoggedIn 
                  ? t('trash.emptyTrash') 
                  : t('trash.pleaseLogin'),
            }}
            scroll={{ x: 800 }}
          />
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default Trash;

